/**
 * 会话上下文状态 —— 「历史看得见」与「模型还记得」是两件事,这里负责把差别**说出来**。
 *
 * 背景:军师的模型上下文在 redis 里有 TTL(≈23h),而用户可见的转录在 MySQL 里长期有。
 * 超时之后会出现:**界面上完整显示着他的历史,模型却一个字都不记得** —— 用户拿着
 * 看得见的那些内容提要求,而模型两眼一抹黑。「看得见」和「算数」在界面上完全同形。
 *
 * ★判据钉在服务端给的 `contextState` 这个**结构事实**上:
 *   - 不钉文案:文案会被改,改完判据还绿;
 *   - 不钉时间:前端自己掐 TTL 迟早跟服务端漂,而且**猜错的方向是静默的** ——
 *     猜"接上了"而实际没接上,界面就正好显示成"像接上了",恰好是这条需求要防的那件事。
 *
 * 契约来自 ai 侧 `POST /advisor/session/state`(只读探针,不建 session、不烧 LLM)。
 */

/** 四档。**不合成 bool**:「没接回」和「只接回一半」是两种不同的坏法,合起来就又同形了。 */
import type { ConfirmGate } from "./chatTypes";

export type ContextState = "restored" | "partial" | "not_restored" | "unavailable";

export interface SessionState {
  sessionId: string;
  /**
   * 本次**要求切到**的话题;没要求切时为 null。
   * ★存在的唯一理由是**让前端能验"是不是我要的那条"** —— 2026-08-19 实测过一个
   * 反例:要 `de2dcd84…`,服务端回 `4edac04a…`,**HTTP 200、字段齐、状态看着完全正常**。
   * 光看"有没有报错"分不出来,只有拿它跟 `topicId` 对才分得出。
   * ★服务端保证:切失败也**回显收到的那个值**,不置 null —— 否则「没要求切」与
   * 「要求了但没切成」又同形了。
   */
  requestedTopicId?: string | null;
  /** 当前话题;取不到时为 null(不是空串)。 */
  topicId: string | null;
  contextState: ContextState;
  /** 模型这边真接回了几轮。 */
  restoredTurns: number;
  /** 转录里一共几轮。**null = 「我不知道」,0 = 「真的没有消息」** —— 这两个不许同形。 */
  totalTurns: number | null;
  /**
   * 此刻还挂着、尚未回答的那张确认卡(1004 A1);没有 / 已过期 / 不归他 = null 或缺省。
   * ★与 /advisor/chat 回包里的 `confirm` 是**同一个对象**(同一个 nonce),刷新后据它把按钮摆回来。
   *   缺了它,刷新后卡面文字还在(转录回灌)但按钮没了,用户只能打字,而那句话会被当成续接旧卡。
   */
  pendingConfirm?: ConfirmGate | null;
}

export interface ContextNotice {
  tone: "warn";
  text: string;
}

/**
 * 该不该开口、开口说什么。
 *
 * @param state 服务端探针的原样响应
 * @param shownHistoryCount 这一刻**屏幕上**摆着几条历史。0 = 没有历史在屏幕上 ⇒
 *   不存在「看得见但不算数」的错觉,就不该无端吓唬用户(全新会话也会落到 unavailable,
 *   那时"我不知道"是这一刻的实话,但没有历史可误导)。
 */
export function contextNotice(
  state: SessionState,
  shownHistoryCount: number
): ContextNotice | null {
  // 屏幕上没有历史 → 没有可被误解的东西,闭嘴
  if (shownHistoryCount <= 0) return null;

  const total = state.totalTurns;
  const restored = state.restoredTurns ?? 0;
  const missing = typeof total === "number" ? Math.max(0, total - restored) : 0;

  switch (state.contextState) {
    case "restored":
      // ★不因为标签写着 restored 就闭嘴:标签漂了的表现正好是「用户以为模型全记得」,而那是静默的。
      //   但**比较方向只能是 `<`,不能是 `!==`**:
      //   模型手上的轮数**可以合法地比转录多** —— 开场简报(session_open)会把开场白 seed 进
      //   模型上下文,而它**不算转录的一轮**。测试机真回包实测:转录 3 轮、模型 4 轮,
      //   `{"contextState":"restored","restoredTurns":4,"totalTurns":3}` —— 这是**健康**会话。
      //   2026-08-20 用 `!==` 上过一次线:凡是带开场简报的会话回灌后都被误报「没接回来」,
      //   而这个功能的全部意义就是不说假话。**多不是问题,少才是问题。**
      //   ⚠️ 下一个人别把它"收敛"回 `!==`。
      if (typeof total === "number" && restored < total) {
        return { tone: "warn", text: missingText(missing) };
      }
      return null;
    case "partial":
      return { tone: "warn", text: missingText(missing) };
    case "not_restored":
      return {
        tone: "warn",
        text: "上面这些历史你看得到,但我这边没接回来 —— 要接着做,麻烦把要点重说一句。",
      };
    case "unavailable":
      return {
        tone: "warn",
        text: "我这会儿读不到这段对话的上下文,拿不准还记不记得 —— 要接着做,麻烦把要点重说一句。",
      };
    default:
      // ★形状认不出往**保守**落:当作没接回来。认成 restored 就是静默放过。
      return {
        tone: "warn",
        text: "我这边的上下文状态拿不准 —— 要接着做,麻烦把要点重说一句。",
      };
  }
}

function missingText(missing: number): string {
  return missing > 0
    ? `更早的 ${missing} 轮我这边没接回来 —— 要提到那部分,麻烦重说一句。`
    : "更早的部分我这边没接回来 —— 要提到那部分,麻烦重说一句。";
}

/**
 * 这次「从历史点开继续」到底切过去了没有。
 *
 * ★判据钉 `topicId === requestedTopicId`,**不钉 HTTP 状态码、不钉有没有报错** ——
 * 那个真实缺陷就是 200 + 字段齐 + 回了另一条。
 */
export function switchedToRequested(state: SessionState, askedFor?: string | null): boolean {
  // ★优先用**我自己发出去的那个 topicId**,不完全依赖对面回显:
  //   回显字段可能被中间层丢掉(实测过 api 代理一度只透传 sessionId、没接 topicId),
  //   那时回包 requestedTopicId=null,只信它就会把"请求根本没送到"读成"没要求切" ——
  //   于是点了按钮什么都不发生、而且一声不吭。两条线索都用,以我发出去的那条为准。
  const want = askedFor ?? state.requestedTopicId;
  if (!want) return false; // 真的没要求切 —— 这不是「失败」,只是没这回事
  return state.topicId === want;
}

/** 要求了却没切过去 → 必须说出来(点开的是 B、系统还在 A,不许装作切了)。 */
export function switchFailedNotice(
  state: SessionState,
  askedFor?: string | null
): ContextNotice | null {
  const want = askedFor ?? state.requestedTopicId;
  if (!want) return null; // 没要求切,就没有「没切成」这回事
  if (state.topicId === want) return null;
  return {
    tone: "warn",
    text: "这段历史没能接过来(可能不是你的,或者当前还有任务在跑)—— 界面还停在原来那段对话上。",
  };
}
